<template>
  <div>
    <v-dialog v-model="dialog" scrollable hide-overlay max-width="600px">
      <v-card class="rounded-lg fill-height">
        <v-container fluid class="pa-7">
          <v-row>
            <v-col>
              <div class="d-flex justify-space-between align-center">
                <div
                  class="font-weight-medium text-h5 text-start textLight--text"
                >
                  {{
                    isCreate
                      ? $t("user.employees.addNewEmployee")
                      : $t("user.employees.updateEmployee")
                  }}
                </div>
                <v-switch
                  style="width: 40px"
                  class="ma-0"
                  flat
                  dense
                  hide-details
                  inset
                  color="success"
                  v-model="employee.user.isActive"
                >
                </v-switch>
              </div>
            </v-col>
          </v-row>

          <v-row class="textLight--text">
            <ValidationObserver ref="observer">
              <v-col cols="12">
                <v-row align="center">
                  <v-col cols="3">
                    <div class="text-start font-weight-regular text-subtitle-2">
                      {{ $t("user.registration.name") }}
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <validation-provider v-slot="{ errors }" rules="required">
                      <v-text-field
                        :error-messages="errors"
                        @click="registerErr = null"
                        class="rounded-lg"
                        v-model="employee.user.firstName"
                        dense
                        hide-details
                        outlined
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="3">
                    <div class="text-start font-weight-regular text-subtitle-2">
                      {{ $t("user.email") }}
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <validation-provider
                      v-slot="{ errors }"
                      rules="email"
                    >
                      <v-text-field
                        @click="registerErr = null"
                        :error-messages="errors"
                        class="rounded-lg"
                        v-model="employee.user.email"
                        dense
                        hide-details
                        outlined
                      ></v-text-field>
                    </validation-provider>
                  </v-col>

                  <v-col cols="3">
                    <div class="text-start font-weight-regular text-subtitle-2">
                      {{ $t("user.civilIdOrPassport") }}
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <validation-provider v-slot="{ errors }" rules="required">
                      <v-text-field
                        @click="registerErr = null"
                        :error-messages="errors"
                        class="rounded-lg"
                        v-model="employee.user.civilIdOrPassport"
                        dense
                        hide-details
                        outlined
                      ></v-text-field>
                    </validation-provider>
                  </v-col>

                  <v-col cols="3">
                    <div class="text-start font-weight-regular text-subtitle-2">
                      {{ $t("user.designation") }}
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <v-text-field
                      @click="registerErr = null"
                      class="rounded-lg"
                      v-model="designation"
                      dense
                      hide-details
                      outlined
                    ></v-text-field>
                  </v-col>

                  <v-col cols="3" class="align-self-start pt-7">
                    <div class="text-start font-weight-regular text-subtitle-2">
                      {{ $t("user.registration.organization") }}
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <validation-provider
                      v-slot="{ errors }"
                      rules="required"
                      style="width: 100%"
                    >
                      <v-autocomplete
                        hide-details
                        @click="registerErr = null"
                        v-model="selectedOrganization"
                        :error-messages="errors"
                        dense
                        :items="organizations"
                        item-value="_id"
                        flat
                        solo
                        outlined
                        :label="$t('user.registration.selectOrganization')"
                        class="rounded-lg"
                        background-color="transparent"
                        :filter="customLocalisedAutoCompleteFilter"
                      >
                        <template slot="selection" slot-scope="data">
                          {{ getLocalisedValue(data.item.name) }}
                        </template>
                        <template v-slot:item="{ item }">
                          <div
                            class="d-flex flex-column text-start"
                            style="width: 100%"
                          >
                            {{ getLocalisedValue(item.name) }}
                            <v-divider></v-divider>
                          </div>
                        </template>
                      </v-autocomplete>
                    </validation-provider>

                    <v-checkbox
                      v-model="employee.user.isOrganizationManager"
                      hide-details
                      :label="$t('user.subRoles.isOrganizationManager')"
                    ></v-checkbox>

                    <div
                      v-if="showOrganizationManagerNote"
                      class="d-flex justify-start"
                      style="padding: 0 3px; font-size: 8px"
                    >
                      <span class="caption info--text text-start">
                        Note: Should add "ORGANIZATION_MANAGER" in Roles.
                      </span>
                    </div>
                  </v-col>

                  <v-col cols="3">
                    <div class="text-start font-weight-regular text-subtitle-2">
                      {{ $t("user.employees.roles") }}
                    </div>
                  </v-col>
                  <v-col cols="9">
                    <v-autocomplete
                      @click="registerErr = null"
                      dense
                      hide-details
                      v-model="rolesSelected"
                      :items="subRolesArr"
                      item-value="_id"
                      flat
                      solo
                      background-color="#e6e6e680"
                      multiple
                      class="rounded-lg"
                      :filter="customLocalisedAutoCompleteFilter"
                    >
                      <template slot="selection" slot-scope="data">
                        <v-chip
                          close
                          text-color="textLight"
                          style="max-width: 220px"
                          color="#28116280"
                          class="me-1 mt-1 rounded-lg"
                          @click:close="deleteChip(data.item._id)"
                        >
                          <span class="text-truncate">
                            {{ data.item.name }}</span
                          >
                        </v-chip>
                      </template>
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item
                          v-on="on"
                          v-bind="attrs"
                          #default="{ active }"
                        >
                          <v-checkbox
                            :input-value="active"
                            :label="item.name"
                          ></v-checkbox>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" v-if="registerErr">
                <p class="pa-0 ma-0 error--text">
                  {{ registerErr }}
                </p>
              </v-col>
              <v-col cols="12">
                <v-btn
                  depressed
                  block
                  min-height="45"
                  color="primary"
                  style="text-transform: unset"
                  class="rounded-lg font-weight-normal"
                  @click="submit"
                  :loading="loading"
                >
                  {{ isCreate ? $t("organization.add") : $t("update") }}
                </v-btn>
              </v-col>
            </ValidationObserver>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import "../../core-module/util/validationRules";
import { updateEmployee } from "../methods/updateEmployee";
import { createEmployee } from "../methods/createEmployees";
export default {
  data() {
    return {
      employee: {},
      rolesSelected: [],
      organizations: [],
      selectedOrganization: null,
      designation: null,
      loading: false,
      registerErr: null,
    };
  },
  props: {
    employeeData: {
      type: Object,
      default: () => {},
    },
    isCreate: {
      type: Boolean,
      default: false,
    },
    subRolesArr: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    showOrganizationManagerNote() {
      return (
        this.employee.user.isOrganizationManager &&
        this.rolesSelected?.findIndex(x => {
          if (x._id) {
            return x.name === "ORGANIZATION_MANAGER";
          } else {
            return (
              this.subRolesArr.find(y => y._id === x)?.name ===
              "ORGANIZATION_MANAGER"
            );
          }
        }) < 0
      );
    },
    dialog: {
      get() {
        return this.employeeData;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      },
    },
  },
  apollo: {
    getOrganizations() {
      return {
        query: require("../../organization-module/api/query/getOrganizations.gql"),
        variables: { limit: 0, skip: 0, filterInput: null },
        result({ data, loading }) {
          if (data?.getOrganizations?.organizations) {
            this.organizations = data.getOrganizations.organizations?.filter(
              x => x.isActive === true
            );
          }
        },
      };
    },
  },
  watch: {
    employeeData: {
      immediate: true,
      handler() {
        this.employee = JSON.parse(JSON.stringify(this.employeeData)) || {};
        this.registerErr = null;
        if (!this.employee?.user) {
          this.employee = {
            ...this.employee,
            user: {
              isOrganizationManager: false,
              isActive: true,
            },
          };
        }
        this.setRolesSelected(
          this.employeeData.user &&
            this.employeeData.user.subRoles &&
            this.employeeData.user.subRoles.length
            ? this.employeeData.user.subRoles
            : []
        );
        this.selectedOrganization = this.employee.organization?._id;
        this.designation = this.employee?.designation;
      },
      deep: true,
    },
  },

  methods: {
    setRolesSelected(existingSubRolesArr) {
      this.rolesSelected = [];
      if (this.subRolesArr && this.subRolesArr.length) {
        this.rolesSelected = this.subRolesArr.filter(role => {
          return existingSubRolesArr.find(existingRole => {
            return role._id === existingRole._id;
          });
        });
      }
    },
    deleteChip(val) {
      if (val) {
        this.rolesSelected = this.rolesSelected.filter(
          role => role._id !== val
        );
      }
    },
    async submit() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        let userData = {
          firstName: this.employee?.user?.firstName,
          email: this.employee?.user?.email,
          civilIdOrPassport: this.employee?.user?.civilIdOrPassport,
          isActive: this.employee?.user?.isActive
            ? this.employee.user.isActive
            : false,
          subRoles: await this.rolesSelected
            .map(obj => {
              if (obj._id) return obj._id;
              else return obj;
            })
            .filter(ele => ele !== undefined),
          isOrganizationManager: this.employee?.user?.isOrganizationManager,
        };
        this.loading = true;

        if (this.employee._id) {
          let data = {
            user: userData,
            organization: this.selectedOrganization,
            designation: this.designation ? this.designation : " ",
          };
          await updateEmployee({
            id: this.employeeData._id,
            input: data,
          }).then(response => {
            this.loading = false;
            if (response.data) this.$emit("close");
            else if (response.error) {
              if (response.error.includes("ALREADY_EXIST"))
                this.registerErr = this.$t("inputRules.emailAlreadyExist");
              // else if (result.error.includes("FIELD_REQUIRED"))
              //   this.registerErr = this.$t("emailOrCompanyEmailNeeded");
              else this.registerErr = this.$t("somethingWentWrong");
            }
          });
        } else {
          let data = {
            user: userData,
            organization: this.selectedOrganization,
            designation: this.designation ? this.designation : " ",
          };
          await createEmployee(data).then(response => {
            this.loading = false;
            if (response.data) {
              this.$emit("close");
              this.$emit("afterCreate");
            } else if (response.error) {
              if (response.error.includes("ALREADY_EXIST"))
                this.registerErr = this.$t("inputRules.emailAlreadyExist");
              // else if (result.error.includes("FIELD_REQUIRED"))
              //   this.registerErr = this.$t("emailOrCompanyEmailNeeded");
              else this.registerErr = this.$t("somethingWentWrong");
            }
          });
        }
      }
    },
  },
};
</script>
<style scoped>
>>> .v-select__selections {
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 112px;
  margin-block: 10px;
}
</style>
